import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { addToCart, updateCartItem } from "../slices/cartSlice";
import {
  useFetchSavedItemsQuery,
  useAddSavedItemMutation,
  useRemoveSavedItemMutation,
} from "../slices/savedItemsSlice";
import { useGetProductByIdQuery } from "../slices/productSlice";
import ProductDescriptionAndReviews from "../components/products/ProductDescriptionAndReviews";
import Success from "../components/message/Success";
import Loader from "../components/utilComponents/Loader";
import Error from "../components/message/Error";
import Button from "../components/utilComponents/Button";
import Input from "../components/utilComponents/Input";
import whatsappIcon from "../assets/images/whatsapp.svg";
import facebookIcon from "../assets/images/facebook.svg";
import xIcon from "../assets/images/x.svg";
import fullGrayStar from "../assets/images/fullGrayStar.svg";
import fullYellowStar from "../assets/images/fullYellowStar.svg";
import filledGrayBookmark from "../assets/images/filledGrayBookmark.svg";
import filledYellowBookmark from "../assets/images/filledYellowBookmark.svg";
import outlinedGrayBookmark from "../assets/images/outlinedGrayBookmark.svg";

const ProductDetails = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { info: customer } = useSelector((state) => state.frontendAuth);

  const { data: product, isLoading, isError } = useGetProductByIdQuery(id);
  const cartItems = useSelector((state) => state.cart?.cartItems || []);
  const isAddingToCart = useSelector(
    (state) => state.cart.isLoading && state.cart.error === null,
  );

  const {
    data: savedItemsData,
    isFetching: isFetchingSavedItems,
    refetch,
  } = useFetchSavedItemsQuery();

  const [addSavedItem] = useAddSavedItemMutation();
  const [removeSavedItem] = useRemoveSavedItemMutation();

  const [mainImage, setMainImage] = useState("");
  const [quantity, setQuantity] = useState(1);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [localIsBookmarked, setLocalIsBookmarked] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  const savedItems = savedItemsData?.savedItems || [];

  const isBookmarked =
    // product && savedItems.some((item) => item.productId._id === product.id);
    product && savedItems.some((item) => item.productId?._id === product?.id);

  useEffect(() => {
    if (product && product.images && product.images.length > 0) {
      setMainImage(product.images[0]);
    }
  }, [product]);

  useEffect(() => {}, [savedItemsData]);

  useEffect(() => {
    if (product && Array.isArray(savedItems)) {
      const bookmarked = savedItems.some(
        (item) => item.productId?._id === product?.id, // Use optional chaining
      );
      setLocalIsBookmarked(bookmarked);
    }
  }, [product, savedItems]);

  const handleIncreaseQuantity = () => setQuantity((prev) => prev + 1);
  const handleDecreaseQuantity = () =>
    setQuantity((prev) => (prev > 1 ? prev - 1 : 1));
  const handleQuantityChange = (e) =>
    setQuantity(parseInt(e.target.value, 10) || 1);
  const handleStoreClick = () =>
    navigate(`/vendors/${product.vendor.storeName}`);

  const handleAddToCart = async () => {
    if (!customer) {
      navigate("/login"); // ✅ Redirect guest users
      return;
    }

    if (!product || !product.vendor) {
      console.error("Product or vendor information is missing!");
      setErrorMessage("Product or vendor information is missing!");
      return;
    }

    const cartItem = {
      productId: product.id,
      qty: quantity,
      price: product.discountedPrice || product.originalPrice,
      vendorId: product.vendor.vendorId || product.vendor._id,
      storeName: product.vendor.storeName,
    };

    try {
      if (cartItems.find((item) => item.productId === product.id)) {
        // Update the cart item if it already exists
        await dispatch(updateCartItem(cartItem)).unwrap();
        setSuccessMessage("Cart item updated successfully!");
      } else {
        // Add a new cart item
        await dispatch(addToCart(cartItem)).unwrap();
        setSuccessMessage("Product added to cart successfully!");
      }
    } catch (error) {
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "An unexpected error occurred.";
      console.error("Failed to add to cart:", errorMessage);
      setErrorMessage(errorMessage); // Trigger your Error component
    }
  };

  const handleAddBookmark = async () => {
    if (!customer) {
      navigate("/login"); // ✅ Redirect guest users
      return;
    }
    if (isProcessing || !product || !product.id) return;

    setIsProcessing(true);
    try {
      const response = await addSavedItem({ productId: product.id }).unwrap();
      setLocalIsBookmarked(true); // Update local state
      setSuccessMessage(response.message || "Added to saved items"); // Backend message
    } catch (error) {
      console.error("Failed to add saved item:", error);
      setErrorMessage(error.data?.message || "Failed to add to saved items"); // Backend message
    } finally {
      setIsProcessing(false);
    }
  };

  const handleRemoveBookmark = async () => {
    if (isProcessing || !product || !product.id) return;

    setIsProcessing(true);
    try {
      const response = await removeSavedItem({
        productId: product.id,
      }).unwrap();
      setLocalIsBookmarked(false); // Update local state
      setSuccessMessage(response.message || "Removed from saved items"); // Backend message
    } catch (error) {
      console.error("Failed to remove saved item:", error);
      setErrorMessage(
        error.data?.message || "Failed to remove from saved items",
      ); // Backend message
    } finally {
      setIsProcessing(false);
    }
  };

  const handleShare = (platform) => {
    const url = window.location.href;
    const text = `Check out this product: ${product.title}`;
    const shareUrls = {
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
      twitter: `https://twitter.com/share?url=${encodeURIComponent(url)}&text=${encodeURIComponent(text)}`,
      whatsapp: `https://wa.me/?text=${encodeURIComponent(text)} ${encodeURIComponent(url)}`,
    };
    window.open(shareUrls[platform], "_blank");
  };

  if (isLoading || isFetchingSavedItems) {
    return (
      <div>
        <div className=" md:hidden">
          <Loader count={1} columns={1} itemClass="h-[320px]" />;
          <Loader count={1} columns={1} itemClass="h-[200px]" />;
        </div>
        <div className="hidden md:block">
          <Loader count={2} columns={2} itemClass="h-[420px]" />;
          <Loader count={1} columns={1} itemClass="h-[420px]" />;
        </div>
      </div>
    );
  }

  if (isError || !product) {
    return <div className="error">Failed to load product details.</div>;
  }

  const discountPercentage =
    product?.discountedPrice && product?.originalPrice
      ? Math.round(
          ((product.originalPrice - product.discountedPrice) /
            product.originalPrice) *
            100,
        )
      : null;

  return (
    <div className="product-details-container p-4 md:p-6 lg:p-8">
      {/* Back Button */}
      {/* <span className="back-button mb-4" onClick={() => navigate(-1)}>
        <span className="material-symbols-rounded hover:agLightGray text-2xl">
          arrow_back
        </span>
      </span> */}

      <div className="product-content flex flex-col md:flex-row gap-6">
        {/* Product Images */}
        <div className="product-images flex-1">
          {mainImage && (
            <img
              src={mainImage}
              alt={product.title}
              className="w-full mx-auto max-w-[430px] h-[324px] md:max-w-[512px] md:h-[512px] object-cover md:object-cover rounded-lg rounded-md"
            />
          )}
          {product.images.length > 1 && (
            <div className="thumbnail-gallery flex gap-2  mt-4  mb-4 md:mb-12 md:ml-4">
              {product.images.map((image, index) => (
                <img
                  key={index}
                  src={image}
                  alt={`${product.title} ${index + 1}`}
                  className={`thumbnail w-16 h-16 md:w-20 md:h-20 rounded-md cursor-pointer ${mainImage === image ? "border-2 border-agPrimary" : ""}`}
                  onClick={() => setMainImage(image)}
                />
              ))}
            </div>
          )}
        </div>

        {/* Product Information */}
        <div className="product-info">
          <h1 className="text-agBlack text-xl md:text-2xl font-nunito font-regular mb-2">
            {product.title}
          </h1>

          <div className="flex items-center space-x-4 mb-4">
            <p className="text-xs text-agLightGray">
              by{" "}
              <span
                className="text-agBlue cursor-pointer"
                onClick={handleStoreClick}
              >
                {product.vendor?.storeName || "Unknown Store"}
              </span>
            </p>
            {product.isTopSelling && (
              <span className="bg-green-100 text-green-800 text-xs px-2 py-1 rounded-md">
                Top selling
              </span>
            )}
            {product.isSponsored && (
              <span className="bg-agRedBg text-agBlack text-xs px-2 py-1 rounded-md">
                Sponsored
              </span>
            )}
          </div>

          <div className="flex items-center mb-2 justify-between">
            <div className="flex items-center">
              <span className="material-symbols-rounded text-agPrimary font-thin">
                location_on
              </span>
              <p className="text-agLightGray font-thin text-xs">
                {product.warehouseState}
              </p>
            </div>

            {/* Bookmark product */}
            <div className="bookmark-icon">
              {isProcessing ? (
                // <img
                //   src={outlinedGrayBookmark} // Use a neutral icon or spinner during processing
                //   alt="Processing Bookmark"
                //   className="w-8 h-8"
                // />
                <div className="bookmark-loader p-4"></div> // Custom loader
              ) : localIsBookmarked ? (
                <img
                  src={filledYellowBookmark} // Highlighted bookmark icon for saved items
                  alt="Remove Bookmark"
                  className="w-8 h-8 cursor-pointer"
                  onClick={handleRemoveBookmark}
                />
              ) : (
                <img
                  src={outlinedGrayBookmark} // Default gray bookmark icon
                  alt="Add Bookmark"
                  className="w-8 h-8 cursor-pointer"
                  onClick={handleAddBookmark}
                />
              )}
            </div>
          </div>
          <hr className="agBorder mb-4" />

          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-2">
              <span className="text-3xl font-semibold text-agBlack font-nunito">
                <span className="text-xl">₦</span>
                {product.discountedPrice.toLocaleString() ||
                  product.originalPrice.toLocaleString()}
              </span>
              {product.discountedPrice && (
                <>
                  <span className="line-through text-agLightGray text-sm">
                    <span className="text-xs">₦</span>
                    {product.originalPrice.toLocaleString()}
                  </span>
                  <span className="bg-agRedBg text-agRed px-1 py-0.5 rounded-md text-xs">
                    -{discountPercentage}%
                  </span>
                </>
              )}
            </div>
            <div className="flex items-center space-x-2">
              <div>
                <span
                  className="material-symbols-rounded text-agPrimary cursor-pointer"
                  onClick={handleDecreaseQuantity}
                >
                  remove
                </span>
              </div>
              <Input
                type="number"
                value={quantity}
                onChange={handleQuantityChange}
                className="text-center w-8 text-agBlack"
              />
              <div>
                <span
                  className="material-symbols-rounded text-agPrimary cursor-pointer"
                  onClick={handleIncreaseQuantity}
                >
                  add
                </span>
              </div>
            </div>
          </div>
          <p className="text-agLightGray text-sm mb-2">
            {product.quantity} unit(s) left
          </p>

          <div className="actions flex space-x-2 mb-4">
            {/* Add to Cart */}
            <Button
              variant="primary"
              fullWidth
              onClick={handleAddToCart}
              isLoading={isAddingToCart}
            >
              ADD TO CART
            </Button>

            {!localIsBookmarked && (
              <Button
                variant="secondary"
                fullWidth
                onClick={handleAddBookmark}
                isLoading={isProcessing}
              >
                SAVE PRODUCT
              </Button>
            )}

            {localIsBookmarked && (
              <Button
                variant="secondary"
                fullWidth
                onClick={handleRemoveBookmark}
                isLoading={isProcessing}
              >
                REMOVE PRODUCT
              </Button>
            )}
            {successMessage && (
              <Success
                message={successMessage}
                onClose={() => setSuccessMessage("")}
              />
            )}
            {errorMessage && (
              <Error
                message={errorMessage}
                onClose={() => setErrorMessage("")}
              />
            )}

            {successMessage && (
              <Success
                message={successMessage}
                onClose={() => setSuccessMessage("")}
              />
            )}
          </div>

          {/* Ratings and Reviews */}
          {/* <div className="ratings flex items-center justify-between mb-4">
            <div className="flex items-center space-x-4">
              <div className="flex">
                {[1, 2, 3, 4, 5].map((star) => (
                  <img
                    key={star}
                    src={
                      star <= Math.round(product.averageRating)
                        ? fullYellowStar
                        : fullGrayStar
                    }
                    alt={`${star} star`}
                    className="w-5 h-5"
                  />
                ))}
              </div>
              <span className="text-xs text-agBlue cursor-pointer">
                {product.numReviews > 0
                  ? `(${product.numReviews} reviews)`
                  : "No reviews yet"}
              </span>
            </div>
            <div>
              <a href="#" className="text-agBlue font-thin text-xs">
                bulk purchase
              </a>
            </div>
          </div> */}
          <div className="ratings flex items-center justify-between mb-4">
            {product.numReviews > 0 ? (
              <div className="flex items-center space-x-4">
                <div className="flex">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <img
                      key={star}
                      src={
                        star <= Math.round(product.averageRating)
                          ? fullYellowStar
                          : fullGrayStar
                      }
                      alt={`${star} star`}
                      className="w-5 h-5"
                    />
                  ))}
                </div>
                <span className="text-xs text-agBlue cursor-pointer">
                  ({product.numReviews} reviews)
                </span>
              </div>
            ) : (
              <div className="text-xs text-agLightGray">No reviews yet</div>
            )}
            <div>
              <a href="#" className="text-agBlue font-thin text-xs">
                bulk purchase
              </a>
            </div>
          </div>

          <hr className="border mb-2" />

          <div className="share flex flex-col items-start space-y-2">
            <p className="text-agLightGray text-sm">Share with friends</p>
            <div className=" flex space-x-2 share icon cursor pointer">
              <img
                src={facebookIcon}
                alt="Facebook"
                className="hover:opacity-80"
                onClick={() => handleShare("facebook")}
              />
              <img
                src={xIcon}
                alt="Twitter"
                className="hover:opacity-80"
                onClick={() => handleShare("twitter")}
              />
              <img
                src={whatsappIcon}
                alt="WhatsApp"
                className="hover:opacity-80"
                onClick={() => handleShare("whatsapp")}
              />
            </div>
          </div>
        </div>
      </div>
      <ProductDescriptionAndReviews product={product} />
    </div>
  );
};

export default ProductDetails;
